import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import SmsMarketingConsent from "./SmsMarketingConsent";
import { ThemeContext, LanguageContext, UserContext, ActionContext } from "../../contexts/Context";
import { IoCloseOutline, IoLogoInstagram } from "react-icons/io5";
import { getDobInputValue, validateRegistrationDob } from "../../utils/registrationDob";
import {
    getAssetUrl,
    getCompanyAddress,
    getCompanyPhone,
    getInstagramInfo,
    getOperatingHours
} from "./loginDisplayUtil";

const splitCustomerName = (customerName = "") => {
    const nameParts = String(customerName || "").trim().split(/\s+/).filter(Boolean);
    return {
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ")
    };
};

const getFormCustomer = (customer) => {
    const safeCustomer = customer || {};
    const nameParts = splitCustomerName(safeCustomer.customerName);
    const hasOwnValue = (key) => Object.prototype.hasOwnProperty.call(safeCustomer, key);
    return {
        ...safeCustomer,
        firstName: hasOwnValue("firstName") ? safeCustomer.firstName || "" : nameParts.firstName || "",
        lastName: hasOwnValue("lastName") ? safeCustomer.lastName || "" : nameParts.lastName || "",
        phone: safeCustomer.phone || "",
        email: safeCustomer.email || "",
        dob: safeCustomer.dob || 0,
        gender: safeCustomer.gender == null ? 0 : safeCustomer.gender,
        userPassword: safeCustomer.userPassword || "",
        smsMarketingConsent: safeCustomer.smsMarketingConsent === true,
        pdpa: safeCustomer.pdpa || 0
    };
};

const buildCustomerName = (customer) => {
    return [customer.firstName, customer.lastName].filter(Boolean).join(" ").trim() || customer.customerName || "";
};


const CustomerRegister = ({ show, setShow, setShowError, setError, onSubmit, setShowPdpa }) => {
    const [displayShow, setDisplay] = useState(show ? "flex" : "none");
    const themeContext = React.useContext(ThemeContext);
    const languageContext = React.useContext(LanguageContext);
    const {
        lanIndex, customer, company, bgImg, landingImg, otherUrls, openingTime, closingTime,
        mustDob, hideOptionalField, mustEmail
    } = React.useContext(UserContext);
    const { addCustomer } = React.useContext(ActionContext);
    const [passwordConfirm, setPasswordConfirm] = useState("");

    useEffect(() => {
        setDisplay(show ? "flex" : "none");
    }, [show]);

    const handleChange = (value, name) => {
        const nextCustomer = {
            ...getFormCustomer(customer),
            [name]: value
        };

        if (name === "firstName" || name === "lastName") {
            nextCustomer.customerName = buildCustomerName(nextCustomer);
        }

        addCustomer(nextCustomer);
    };

    const handleSubmit = () => {
        const submitCustomer = {
            ...getFormCustomer(customer)
        };
        submitCustomer.customerName = buildCustomerName(submitCustomer);

        if (submitCustomer.phone === "" || submitCustomer.phone.length < 7 || submitCustomer.phone.length > 15) {
            setError([{ errorMessage: "Phone no cannot be less than 7 or greater than 15" }]);
            return setShowError(true);
        }
        if (submitCustomer.pdpa === 0 || submitCustomer.pdpa === "0") {
            setError([{ errorMessage: "Must agree the terms & condition" }]);
            return setShowError(true);
        }
        if (!hideOptionalField && submitCustomer.userPassword !== passwordConfirm) {
            setError([{ errorMessage: "Password confirmation does not match" }]);
            return setShowError(true);
        }
        if (mustEmail) {
            if (submitCustomer.email === "" || submitCustomer.email.includes("@") === false ||
                submitCustomer.email.includes(".") === false) {
                setError([{ errorMessage: "Invalid Email" }]);
                return setShowError(true);
            }
        }
        const dobResult = validateRegistrationDob(submitCustomer.dob, mustDob);
        if (dobResult.error) {
            setError([{ errorMessage: dobResult.error }]);
            return setShowError(true);
        }
        submitCustomer.dob = dobResult.value;
        if (!hideOptionalField && (submitCustomer.email === "" || submitCustomer.email == null || typeof submitCustomer.email === "undefined")) {
            setError([{ errorMessage: "Email is required" }]);
            return setShowError(true);
        }

        onSubmit(submitCustomer);
    };

    const formCustomer = getFormCustomer(customer);
    const operatingHours = getOperatingHours(openingTime, closingTime);
    const companyAddress = getCompanyAddress(company);
    const companyPhone = getCompanyPhone(company);
    const instagramInfo = getInstagramInfo(otherUrls);

    return (
        <React.Fragment>
            <ModalContainer display={displayShow} bgImg={bgImg} themeContext={themeContext}>
                <IconButton onClick={() => setShow(false)}>
                    <IoCloseOutline size={34} />
                </IconButton>
                <ContentWrapper>
                    <FormSection>
                        <ImgWrapper image={company && company.companyLogo} />
                        <TitleText>CREATE A NEW ACCOUNT</TitleText>
                        <Divider />
                        {!hideOptionalField &&
                            <React.Fragment>
                                <SectionTitle>PERSONAL INFORMATION</SectionTitle>
                                <GenderRow>
                                    <RadioLabel>
                                        <RadioInput
                                            name="gender"
                                            value={0}
                                            checked={`${formCustomer.gender}` === "0"}
                                            type="radio"
                                            onChange={(e) => { handleChange(e.target.value, "gender") }}
                                        />
                                        Mr.
                                    </RadioLabel>
                                    <RadioLabel>
                                        <RadioInput
                                            name="gender"
                                            value={1}
                                            checked={`${formCustomer.gender}` === "1"}
                                            type="radio"
                                            onChange={(e) => { handleChange(e.target.value, "gender") }}
                                        />
                                        Mrs.
                                    </RadioLabel>
                                </GenderRow>
                                <Input
                                    value={formCustomer.firstName}
                                    type="text"
                                    onChange={(e) => { handleChange(e.target.value, "firstName") }}
                                    placeholder="First Name*"
                                    aria-label="First Name"
                                />
                                <Input
                                    value={formCustomer.lastName}
                                    type="text"
                                    onChange={(e) => { handleChange(e.target.value, "lastName") }}
                                    placeholder="Last Name*"
                                    aria-label="Last Name"
                                />
                            </React.Fragment>
                        }
                        <Input
                            value={formCustomer.phone}
                            type="tel"
                            onChange={(e) => { handleChange(e.target.value, "phone") }}
                            placeholder={`${languageContext[lanIndex].phone}*`}
                            aria-label={languageContext[lanIndex].phone}
                        />
                        {(mustEmail || !hideOptionalField) &&
                            <Input
                                value={formCustomer.email}
                                type="email"
                                onChange={(e) => { handleChange(e.target.value, "email") }}
                                placeholder={`${languageContext[lanIndex].email}${mustEmail || !hideOptionalField ? "*" : ""}`}
                                aria-label={languageContext[lanIndex].email}
                            />
                        }
                        {!hideOptionalField &&
                            <React.Fragment>
                                <Input
                                    value={formCustomer.userPassword}
                                    type="password"
                                    onChange={(e) => { handleChange(e.target.value, "userPassword") }}
                                    placeholder="Password*"
                                    aria-label="Password"
                                />
                                <Input
                                    value={passwordConfirm}
                                    type="password"
                                    onChange={(e) => { setPasswordConfirm(e.target.value) }}
                                    placeholder="Password Confirmation*"
                                    aria-label="Password Confirmation"
                                />
                            </React.Fragment>
                        }
                        {mustDob &&
                            <Input
                                value={getDobInputValue(formCustomer && formCustomer.dob)}
                                type="text"
                                onChange={(e) => { handleChange(e.target.value, "dob") }}
                                inputMode="numeric"
                                maxLength={8}
                                required={Boolean(mustDob)}
                                placeholder={`${languageContext[lanIndex].dob} (DDMMYYYY)`}
                                aria-label={languageContext[lanIndex].dob}
                            />
                        }
                        <SmsMarketingConsent
                            checked={formCustomer && formCustomer.smsMarketingConsent === true}
                            onChange={(checked) => handleChange(checked, "smsMarketingConsent")}
                        />
                        <TermsRow>
                            <TermsCheckbox
                                id="pdpa"
                                name="pdpa"
                                checked={formCustomer.pdpa === 1 || formCustomer.pdpa === "1"}
                                type="checkbox"
                                onChange={(e) => { handleChange(e.target.checked ? 1 : 0, "pdpa") }}
                            />
                            <TermsLabel htmlFor="pdpa">
                                {languageContext[lanIndex].agreePdpa}
                                <DetailLink onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowPdpa(true); }}>
                                    see detail
                                </DetailLink>
                            </TermsLabel>
                        </TermsRow>
                        <RegisterButton onClick={handleSubmit}>
                            CREATE ACCOUNT
                        </RegisterButton>
                    </FormSection>
                    {(landingImg || operatingHours || companyPhone || companyAddress || instagramInfo) &&
                        <FooterInfo>
                            {landingImg && <FooterLandingImage image={landingImg} />}
                            {operatingHours && <FooterLine>{operatingHours}</FooterLine>}
                            {companyPhone && <FooterLine>{companyPhone}</FooterLine>}
                            {companyAddress && <FooterAddress>{companyAddress}</FooterAddress>}
                            {instagramInfo && instagramInfo.url &&
                                <InstagramLink href={instagramInfo.url} target="_blank" rel="noopener noreferrer">
                                    <IoLogoInstagram size={46} />
                                    {instagramInfo.handle && <InstagramHandle>{instagramInfo.handle.split("?")[0]}</InstagramHandle>}
                                </InstagramLink>
                            }
                        </FooterInfo>
                    }
                </ContentWrapper>
            </ModalContainer>
        </React.Fragment>
    );
};

export default CustomerRegister;

const RegisterButton = styled.div`
    display: flex;
    width: 68%;
    max-width: 360px;
    min-height: 62px;
    margin: 14px auto 0;
    justify-content: center;
    align-items: center;
    font-size: 22px;
    font-weight: 400;
    border-radius: 34px;
    background-color: #5f5f5f;
    color: #ffffff;
    box-shadow: 0px 2px 5px rgba(0,0,0,0.18);
    cursor: pointer;

    @media (max-height: 760px) {
        min-height: 54px;
        font-size: 20px;
    }
`;

const Input = styled.input`
    width: 100%;
    min-height: 64px;
    border-radius: 12px;
    padding: 0 20px;
    font-size: 20px;
    border: 1px solid #9d9d9d;
    margin-bottom: 12px;
    background-color: rgba(255,255,255,0.94);
    color: #343434;
    box-sizing: border-box;
    outline: none;

    &::placeholder {
        color: #a9a9a9;
    }

    @media (max-height: 760px) {
        min-height: 56px;
        font-size: 18px;
        margin-bottom: 10px;
    }
`;

const ImgWrapper = styled.div`
    width: 220px;
    max-width: 78%;
    height: 110px;
    margin: 0 0 20px;
    background-image: ${(props) => props.image ? `url(${getAssetUrl(props.image)})` : "none"};
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    @media (max-height: 760px) {
        width: 180px;
        height: 88px;
        margin-bottom: 14px;
    }
`;

const IconButton = styled.div`
    position: absolute;
    right: 20px;
    top: 20px;
    z-index: 99;
    color: #555555;
    cursor: pointer;
`;

const TitleText = styled.div`
    color: #333333;
    font-size: 28px;
    font-weight: 300;
    line-height: 1;
    text-align: center;

    @media (max-height: 760px) {
        font-size: 24px;
    }
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    margin: 24px 0 20px;
    background-color: rgba(80,80,80,0.45);
`;

const SectionTitle = styled.div`
    align-self: flex-start;
    margin-bottom: 24px;
    font-size: 17px;
    font-weight: 700;
    color: #444444;
    letter-spacing: 0;
`;

const GenderRow = styled.div`
    display: flex;
    align-self: flex-start;
    gap: 18px;
    margin-bottom: 18px;
`;

const RadioLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 18px;
    color: #444444;
`;

const RadioInput = styled.input`
    width: 22px;
    height: 22px;
    margin: 0;
`;

const TermsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    margin: 6px 0 2px;
`;

const TermsCheckbox = styled.input`
    width: 24px;
    height: 24px;
    margin: 0;
`;

const TermsLabel = styled.label`
    font-size: 16px;
    font-weight: 600;
    color: #444444;
`;

const DetailLink = styled.small`
    margin-left: 8px;
    color: #555555;
    font-size: 12px;
    font-weight: 400;
    text-decoration: underline;
    cursor: pointer;
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100dvh;
    box-sizing: border-box;
    padding: 44px 32px 34px;
    overflow-y: auto;
    gap: 34px;

    @media (max-height: 760px) {
        padding-top: 28px;
        gap: 24px;
    }
`;

const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 500px;
    padding-top: 16px;
`;

const FooterInfo = styled.div`
    width: 100%;
    max-width: 520px;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #555555;
    text-align: center;
`;

const FooterLandingImage = styled.div`
    width: 80%;
    height: 100px;
    margin-top: 100px;
    background-image: ${(props) => props.image ? `url(${getAssetUrl(props.image)})` : "none"};
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    @media (max-height: 760px) {
        height: 72px;
    }
`;

const FooterLine = styled.div`
    margin-top: 6px;
    font-size: 21px;
    line-height: 1.15;
`;

const FooterAddress = styled.div`
    max-width: 430px;
    margin-top: 10px;
    font-size: 15px;
    font-style: italic;
    line-height: 1.25;
`;

const InstagramLink = styled.a`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-top: 42px;
    color: #555555;
    text-decoration: none;

    @media (max-height: 760px) {
        margin-top: 24px;
    }
`;

const InstagramHandle = styled.div`
    font-family: "contralto-xsmall", sans-serif;
    font-size: 14px;
`;

const ModalContainer = styled.div`
    display: ${(props) => props.display};
    font-family: "contralto-xsmall", sans-serif;
    position: fixed;
    flex-direction: column;
    background-color: ${(props) => props.themeContext.backgroundColor};
    background-image: ${(props) => props.bgImg ? `url(${getAssetUrl(props.bgImg)})` : "none"};
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    top: 0;

    width: 100vw;
    max-width: 550px;
    height: 100dvh;
    z-index: 8;

    *, input, button, textarea, select {
        font-family: "contralto-xsmall", sans-serif !important;
    }

    animation: modalOrder .1s;
    animation-timing-function: ease-out;
    @keyframes modalOrder {
        0%  {
        transform: translateY(350px);
        opacity: 0;
        }

        100%  {
        transform: translateY(0px);
        opacity: 1;
        }
    }
`;
